import 'bootstrap-icons/font/bootstrap-icons.css';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { useEffect, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import '../css/FoodOrders.css';
import useIdleLogout from "../hooks/useIdleLogout";

ChartJS.register(ArcElement, Tooltip, Legend);

const PALETTE = [
  '#0d6efd', '#198754', '#dc3545', '#ffc107', '#20c997',
  '#6f42c1', '#fd7e14', '#0dcaf0', '#6610f2', '#1982c4'
];

const PAGE_LIMIT = 20;

const unique = (list) => Array.from(new Set(list.filter(Boolean)));

const formatAmt = (n) =>
  `BTN.\u00A0${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function formatPlaced(ts) {
  const d = new Date(ts);
  return d.toLocaleString('en-US', {
    timeZone: 'Asia/Thimphu',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/* -------- status helpers -------- */
const normStatus = (s) => String(s || '').trim().toLowerCase();
const isDelivered = (s) => ['delivered', 'completed'].includes(normStatus(s));
const isRejected = (s) => ['rejected', 'declined'].includes(normStatus(s)); // <- use REJECTED
const cleanStatus = (s) => normStatus(s).replace(/[\s_-]+/g, ' ');

/* moved outside to avoid hook warning */
const INPROGRESS_SET = new Set([
  'pending', 'preparing', 'ready', 'accepted', 'assigned', 'processing',
  'out for delivery', 'shipped'
]);

/* -------- auth header helper -------- */
function getBearerToken() {
  const directToken =
    localStorage.getItem('access_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken') ||
    sessionStorage.getItem('access_token') ||
    sessionStorage.getItem('token') ||
    sessionStorage.getItem('adminToken') ||
    sessionStorage.getItem('accessToken') ||
    sessionStorage.getItem('authToken');

  if (directToken) return String(directToken).trim();

  try {
    const rawUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    const user = rawUser ? JSON.parse(rawUser) : {};

    return String(
      user?.token ||
      user?.adminToken ||
      user?.accessToken ||
      user?.access_token ||
      user?.authToken ||
      user?.jwt ||
      ''
    ).trim();
  } catch {
    return '';
  }
}

function getAuthHeaders() {
  const token = getBearerToken();

  const headers = {
    Accept: 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

export default function FoodOrders() {
  useIdleLogout();

  const API_URL = (process.env.REACT_APP_FOOD_ORDERS_API_URL || '').trim();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // toolbar state
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // fetch
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr('');
        if (!API_URL) {
          throw new Error('Food Orders API URL is not configured. Check REACT_APP_FOOD_ORDERS_API_URL in .env');
        }

        const res = await fetch(API_URL, {
          method: 'GET',
          headers: getAuthHeaders(),
          cache: 'no-store',
        });

        if (!res.ok) {
          let message = `HTTP ${res.status}`;
          try {
            const errorJson = await res.json();
            message = errorJson?.message || errorJson?.error || message;
          } catch {
            try {
              const errorText = await res.text();
              if (errorText) message = errorText.slice(0, 160);
            } catch { }
          }

          if (res.status === 401) {
            throw new Error(`Unauthorized (401): Please login again. ${message}`);
          }

          if (res.status === 403) {
            throw new Error(`Forbidden (403): You do not have permission to view food orders. ${message}`);
          }

          throw new Error(message);
        }

        const json = await res.json();

        const rows = Array.isArray(json?.data) ? json.data : [];
        const normalized = rows.map((r, i) => ({
          id: i + 1,
          orderId: r.Order_ID ?? r.order_id ?? r.orderId,
          customerName: r.Customer_Name ?? r.customer_name ?? r.customer,
          businessName: r.Business_Name ?? r.business_name ?? r.restaurant ?? r.business,
          itemsName: r.Items_Name ?? r.items_name ?? r.itemsName ?? '',
          totalQty: Number(r.Total_Quantity ?? r.total_quantity ?? r.itemsCount ?? 0),
          totalAmount: Number(r.Total_Amount ?? r.total_amount ?? r.amount ?? 0),
          payment: r.Payment ?? r.payment,
          status: r.Status ?? r.status,
          placedAt: r.Placed_At ?? r.placed_at ?? r.placedAt
        }));
        if (alive) setOrders(normalized);
      } catch (e) {
        if (alive) setErr(`Failed to load orders: ${e.message}`);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [API_URL]);

  // filter (search + date)
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const from = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
    const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;

    return orders.filter(o => {
      const hay = `${o.orderId} ${o.customerName} ${o.businessName} ${o.itemsName}`.toLowerCase();
      const okText = !term || hay.includes(term);

      const d = new Date(o.placedAt);
      const okFrom = !from || d >= from;
      const okTo = !to || d <= to;

      return okText && okFrom && okTo;
    });
  }, [orders, searchTerm, dateFrom, dateTo]);

  /* ---------- TABLE subset: only delivered/completed & REJECTED ---------- */
  const tableAllRows = useMemo(
    () => filtered.filter(o => isDelivered(o.status) || isRejected(o.status)),
    [filtered]
  );
  const tableRows = useMemo(() => tableAllRows.slice(0, PAGE_LIMIT), [tableAllRows]);

  /* ---------- METRICS ---------- */
  const totalOrders = filtered.length;
  const inProgress = useMemo(
    () => filtered.reduce((n, o) => (INPROGRESS_SET.has(cleanStatus(o.status)) ? n + 1 : n), 0),
    [filtered]
  );
  const delivered = useMemo(
    () => tableAllRows.filter(o => isDelivered(o.status)).length,
    [tableAllRows]
  );
  const rejected = useMemo(
    () => tableAllRows.filter(o => isRejected(o.status)).length,
    [tableAllRows]
  );

  /* ========================= CHARTS ========================= */
  // LEFT donut: group by Business_Name (businessName) using all filtered rows (unchanged)
  const bizNameLabels = useMemo(() => {
    const map = new Map();
    for (const o of filtered) {
      const raw = (o.businessName || '').toString().trim();
      if (!raw) continue;
      const key = raw.toLowerCase();
      if (!map.has(key)) map.set(key, raw);
    }
    return Array.from(map.values());
  }, [filtered]);

  const businessNamesData = useMemo(() => {
    const labels = bizNameLabels.length ? bizNameLabels : ['No Data'];
    const counts = labels[0] === 'No Data'
      ? [1]
      : labels.map(lbl =>
        filtered.filter(o => (o.businessName || '').toString().trim().toLowerCase() === lbl.toLowerCase()).length
      );
    return {
      labels,
      datasets: [{
        data: counts,
        backgroundColor: labels.map((_, i) => PALETTE[i % PALETTE.length]),
      }],
    };
  }, [bizNameLabels, filtered]);

  /* ---------- CHANGED: Payment donut uses ALL filtered completed (not just current page) ---------- */
  const completedRows = useMemo(
    () => filtered.filter(o => isDelivered(o.status)),
    [filtered]
  );

  const paymentLabels = useMemo(
    () => unique(completedRows.map(o => o.payment || 'Unknown')),
    [completedRows]
  );

  const paymentData = useMemo(() => ({
    labels: paymentLabels.length ? paymentLabels : ['No Data'],
    datasets: [{
      data:
        paymentLabels.length
          ? paymentLabels.map(p =>
            completedRows.filter(o => (o.payment || 'Unknown') === p).length
          )
          : [1],
      backgroundColor: (paymentLabels.length ? paymentLabels : ['No Data']).map((_, i) => PALETTE[(i + 3) % PALETTE.length])
    }]
  }), [paymentLabels, completedRows]);

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    layout: { padding: { bottom: 10 } },
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, padding: 10, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const label = ctx.label || '';
            const value = ctx.raw ?? 0;
            return `${label}: ${value}`;
          }
        }
      }
    }
  };

  /* ---------- EXPORTS (all filtered, not just tableRows) ---------- */
  const rangeText = `${dateFrom || 'All'} to ${dateTo || 'All'}`;
  const exportedAtText = new Date().toLocaleString('en-US', {
    timeZone: 'Asia/Thimphu',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
  const fileDate = new Date().toISOString().slice(0, 10);
  const rangeSlug = `${(dateFrom || 'ALL')}_to_${(dateTo || 'ALL')}`;

  const handleExportCSV = () => {
    const title = `Food Order Table List (${rangeText}) • Exported: ${exportedAtText}`;
    const rows = [
      [title],
      [],
      ['SI.NO', 'Order ID', 'Customer Name', 'Business Types', 'Items Name', 'Total Quantity', 'Total Amount', 'Payment', 'Status', 'Placed At'],
      ...filtered.map((o, i) => [
        i + 1, o.orderId, o.customerName, o.businessName, o.itemsName,
        o.totalQty, Number(o.totalAmount), o.payment, o.status, formatPlaced(o.placedAt)
      ])
    ];
    const csv = rows.map(r => r.map(v => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `food_orders_${rangeSlug}_exported_${fileDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      const doc = new jsPDF('l', 'pt', 'a4');
      doc.setFontSize(14);
      doc.text(`Food Order Table List (${rangeText})`, 40, 36);
      doc.setFontSize(10);
      doc.text(`Exported: ${exportedAtText}`, 40, 52);
      autoTable(doc, {
        startY: 66,
        head: [['SI.NO', 'Order ID', 'Customer Name', 'Business Types', 'Items Name', 'Total Quantity', 'Total Amount', 'Payment', 'Status', 'Placed At']],
        body: filtered.map((o, i) => [
          i + 1, o.orderId, o.customerName, o.businessName, o.itemsName,
          o.totalQty, formatAmt(o.totalAmount), o.payment, o.status, formatPlaced(o.placedAt)
        ]),
        styles: { fontSize: 10 },
        theme: 'grid',
        headStyles: { fillColor: [13, 110, 253] }
      });
      doc.save(`food_orders_${rangeSlug}_exported_${fileDate}.pdf`);
    } catch {
      alert('To export PDF, please install:\n\nnpm i jspdf jspdf-autotable');
    }
  };

  return (
    <>
      {err && (<div className="alert alert-danger" role="alert">{err}</div>)}

      {/* Metrics */}
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3 mb-4 orders-metrics">
        {[
          { label: 'Total Orders', value: totalOrders },
          { label: 'In Progress', value: inProgress },
          { label: 'Delivered', value: delivered },
          { label: 'Declined', value: rejected }  // <- label changed
        ].map((m, i) => (
          <div className="col" key={i}>
            <div className="card metrics-card h-100">
              <div className="card-body">
                <h6 className="text-secondary mb-1">{m.label}</h6>
                <div className="fs-2 fw-bold">{m.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="card chart-card h-100">
            <div className="card-header">Business Names</div>
            <div className="card-body">
              <Doughnut data={businessNamesData} options={doughnutOptions} />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card chart-card h-100">
            <div className="card-header">Payment Method Breakdown (Completed Only)</div>
            <div className="card-body">
              <Doughnut data={paymentData} options={doughnutOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="card mb-3 search-filter-card">
        <div className="card-body searchbar-row-inline">
          <span className="search-icon" aria-hidden="true">
            <i className="bi bi-search" />
          </span>

          <div className="search-wrap">
            <input
              type="text"
              className="form-control search-pill"
              placeholder="Search by order id, customer, business, items…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search orders"
            />
          </div>

          <div className="date-range">
            <input
              type="date"
              className="date-pill"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="Start date"
            />
            <span className="range-sep">to</span>
            <input
              type="date"
              className="date-pill"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label="End date"
            />
          </div>

          <div className="exports ms-auto d-flex gap-2">
            <button className="btn btn-success" onClick={handleExportCSV}>Export Excel</button>
            <button className="btn btn-danger" onClick={handleExportPDF}>Export PDF</button>
          </div>
        </div>
      </div>

      {/* Table (ONLY delivered/completed + REJECTED) */}
      <div className="orders-table-card card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Food Orders Record</h5>
        </div>
        <div className="table-responsive">
          <table className="table table-hover align-middle table-pro">
            <colgroup>
              <col style={{ width: '70px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '160px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '190px' }} />
            </colgroup>
            <thead>
              <tr>
                <th>SI.NO</th>
                <th>Order ID</th>
                <th>Customer Name</th>
                <th>Business Types</th>
                <th>Items Name</th>
                <th>Total Quantity</th>
                <th>Total Amount</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Placed At</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="10" className="py-4 text-center text-muted">Loading…</td></tr>
              )}
              {!loading && tableRows.map((o, i) => (
                <tr key={`${o.orderId}-${i}`}>
                  <td>{i + 1}</td>
                  <td className="mono order-id">{o.orderId}</td>
                  <td className="text-truncate" title={o.customerName}>{o.customerName}</td>
                  <td className="text-truncate" title={o.businessName}>{o.businessName}</td>
                  <td className="text-truncate" title={o.itemsName}>{o.itemsName || '-'}</td>
                  <td className="mono">{o.totalQty}</td>
                  <td className="mono nowrap amount-cell">{formatAmt(o.totalAmount)}</td>
                  <td>{o.payment}</td>
                  <td>{o.status ?? '-'}</td>
                  <td title={formatPlaced(o.placedAt)} className="placed-at nowrap">{formatPlaced(o.placedAt)}</td>
                </tr>
              ))}
              {!loading && tableRows.length === 0 && (
                <tr>
                  <td colSpan="10" className="text-center py-3 text-muted">No delivered or rejected orders.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <footer className="dashboard-footer mt-auto">
        <small>© {new Date().getFullYear()}  <strong>སྟབས་བདེ། </strong> Admin • All rights reserved</small>
      </footer>
    </>
  );
}
